/**
 * EmployeesPage — Phase 4c (erweitert Phase 4c+, 27.05.2026).
 *
 * Händler-Settings: Mitarbeiter anlegen, deaktivieren, Passwort ändern,
 * löschen. Mitarbeiter können entweder einen Login haben (Zweiradverkäufer
 * mit Zugang zu Werkstatt-Aufträgen + Kalender) oder reine "Werkstatt-
 * Mitarbeiter" ohne Login sein (für Auftrags-Zuweisung & Stempeluhr).
 *
 * Funktionen: Verkäufer · Kundendienst · Mechaniker
 * Mechaniker-Spezialitäten: Aufbereitung · Lehrling · Mechanik · Verschleiß
 *
 * Route: /dealer/employees
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, Trash2, Check, X as XIcon, ArrowLeft, ShieldOff, Wrench, HeadphonesIcon, Tag, MessageCircle, Phone, Pencil, Upload, Link2, MailPlus } from "lucide-react";

import api from "../lib/api";
import { DEALER_MENU_SECTIONS } from "../lib/dealerMenuConfig";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DepartmentsManager } from "../components/dealer/DepartmentsManager";

// ----- Stamm-Listen für Funktionen + Mechaniker-Spezialitäten -----
const FUNCTIONS = [
    { value: "verkaeufer", label: "Verkauf" },
    { value: "verkaufsleiter", label: "Verkaufsleitung" },
    { value: "kundendienst", label: "Kundendienst" },
    { value: "mechaniker", label: "Werkstatt" },
    { value: "filialleiter", label: "Filialleitung" },
    { value: "gebietsleitung", label: "Gebietsleitung" },
    { value: "disposition", label: "Disposition" },
    { value: "buchhaltung", label: "Buchhaltung" },
    { value: "geschaeftsleitung", label: "Geschäftsleitung" },
    { value: "lagerist", label: "Lagerist" },
];
const SPECIALTIES = [
    { value: "aufbereitung", label: "Aufbereitung" },
    { value: "lehrling", label: "Lehrling" },
    { value: "mechanik", label: "Mechanik" },
    { value: "verschleiss", label: "Verschleiß" },
];

const FN_LABEL = Object.fromEntries(FUNCTIONS.map((f) => [f.value, f.label]));
const SPEC_LABEL = Object.fromEntries(SPECIALTIES.map((s) => [s.value, s.label]));

// Jul-2026 (User-Wunsch) — Menü-Sichtbarkeit pro Mitarbeiter (Header-Dropdown),
// inkl. UNTERPUNKTEN. Automatisch aus der zentralen Navigations-Konfiguration
// (dealerMenuConfig.js) abgeleitet — Erweiterungen des Dropdowns erscheinen
// hier ohne weitere Anpassung.
const _MENU_TOP_KEYS = ["menu-intranet", "menu-workshop-orders", "menu-wms",
    "menu-invoices", "menu-disposition", "menu-buchhaltung"];
const MENU_SECTIONS_CFG = DEALER_MENU_SECTIONS
    .filter((s) => _MENU_TOP_KEYS.includes(s.triggerTestid || s.testid))
    .map((s) => ({
        key: s.triggerTestid || s.testid,
        label: (s.triggerTestid || s.testid) === "menu-buchhaltung" ? `${s.label} (nur Leitung/Buchhaltung)` : s.label,
        items: s.kind === "sub"
            ? s.items.filter((it) => !it.onlineshop).map((it) => ({ key: it.testid, label: it.label }))
            : [],
    }));
// Jul-2026 (User-Wunsch) — Cockpit-Bereiche pro Mitarbeiter freischalten
// (erscheinen im Mitarbeiter-Dashboard unter „Mein Cockpit").
const COCKPIT_SECTIONS_CFG = [
    { key: "cockpit-verkauf", label: "Verkauf" },
    { key: "cockpit-ankauf", label: "Ankauf" },
    { key: "cockpit-werkstatt", label: "Werkstatt" },
    { key: "cockpit-disposition", label: "Disposition" },
    { key: "cockpit-buchhaltung", label: "Buchhaltung" },
    { key: "cockpit-system", label: "System (nur Leitung)" },
];
// Preset je Funktion: Liste der VERSTECKTEN Bereiche (muss dem Backend entsprechen)
const MENU_PRESETS_BY_FUNCTION = {
    mechaniker: ["menu-wms", "menu-invoices", "menu-disposition", "menu-buchhaltung",
                 "cockpit-verkauf", "cockpit-ankauf", "cockpit-disposition", "cockpit-buchhaltung", "cockpit-system"],
    kundendienst: ["menu-disposition", "menu-buchhaltung",
                   "cockpit-ankauf", "cockpit-disposition", "cockpit-buchhaltung", "cockpit-system"],
    verkaeufer: ["menu-workshop-orders", "menu-wms", "menu-buchhaltung",
                 "cockpit-werkstatt", "cockpit-disposition", "cockpit-buchhaltung", "cockpit-system"],
    verkaufsleiter: ["menu-workshop-orders", "menu-buchhaltung",
                     "cockpit-werkstatt", "cockpit-buchhaltung"],
    disposition: ["menu-workshop-orders", "menu-wms", "menu-buchhaltung",
                  "cockpit-verkauf", "cockpit-werkstatt", "cockpit-buchhaltung", "cockpit-system"],
    buchhaltung: ["menu-workshop-orders", "menu-wms", "menu-disposition",
                  "cockpit-verkauf", "cockpit-ankauf", "cockpit-werkstatt", "cockpit-disposition", "cockpit-system"],
    filialleiter: [],
    gebietsleitung: [],
    geschaeftsleitung: [],
};

function MenuSectionsChecklist({ hiddenMenus, onToggle, testIdPrefix }) {
    return (
        <div className="space-y-2">
            {MENU_SECTIONS_CFG.map((m) => {
                const parentVisible = !hiddenMenus.includes(m.key);
                return (
                    <div key={m.key} className="border border-gray-200 rounded-sm bg-white px-2 py-1.5">
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-bold" data-testid={`${testIdPrefix}-menu-${m.key}`}>
                            <input type="checkbox" checked={parentVisible}
                                onChange={() => onToggle(m.key)} className="w-4 h-4"
                                data-testid={`${testIdPrefix}-menu-cb-${m.key}`} />
                            {m.label}
                        </label>
                        {parentVisible && m.items.length > 0 && (
                            <div className="mt-1 ml-6 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                                {m.items.map((it) => (
                                    <label key={it.key} className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-700" data-testid={`${testIdPrefix}-menu-${it.key}`}>
                                        <input type="checkbox" checked={!hiddenMenus.includes(it.key)}
                                            onChange={() => onToggle(it.key)} className="w-3.5 h-3.5"
                                            data-testid={`${testIdPrefix}-menu-cb-${it.key}`} />
                                        {it.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function MenuVisibilityConfig({ hiddenMenus, onToggle, testIdPrefix }) {
    return (
        <div className="rounded-sm border border-gray-200 p-3 bg-gray-50 space-y-3" data-testid={`${testIdPrefix}-menu-config`}>
            <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Sichtbare Menüpunkte (Header-Dropdown)</label>
                <p className="text-[10px] text-gray-500 mt-0.5 mb-2">Abgewählte Bereiche/Unterpunkte erscheinen für diesen Mitarbeiter nicht im Menü.</p>
                <MenuSectionsChecklist hiddenMenus={hiddenMenus} onToggle={onToggle} testIdPrefix={testIdPrefix} />
            </div>
            <div className="border-t border-gray-200 pt-3">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Cockpit-Bereiche (Dashboard „Mein Cockpit")</label>
                <p className="text-[10px] text-gray-500 mt-0.5 mb-2">Nur die aktivierten Bereiche erscheinen im Dashboard dieses Mitarbeiters.</p>
                <div className="grid grid-cols-2 gap-1.5">
                    {COCKPIT_SECTIONS_CFG.map((m) => (
                        <label key={m.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`${testIdPrefix}-menu-${m.key}`}>
                            <input type="checkbox" checked={!hiddenMenus.includes(m.key)}
                                onChange={() => onToggle(m.key)} className="w-4 h-4"
                                data-testid={`${testIdPrefix}-menu-cb-${m.key}`} />
                            {m.label}
                        </label>
                    ))}
                </div>
            </div>
        </div>
    );
}

function FunctionBadge({ fn, specialty }) {
    if (!fn) return <span className="text-gray-300 text-xs">—</span>;
    const tones = {
        verkaeufer: { bg: "bg-blue-100 text-blue-900", icon: <Tag className="w-3 h-3" /> },
        kundendienst: { bg: "bg-purple-100 text-purple-900", icon: <HeadphonesIcon className="w-3 h-3" /> },
        mechaniker: { bg: "bg-amber-100 text-amber-900", icon: <Wrench className="w-3 h-3" /> },
        filialleiter: { bg: "bg-emerald-100 text-emerald-900", icon: <Users className="w-3 h-3" /> },
    };
    const t = tones[fn] || { bg: "bg-gray-100 text-gray-800", icon: null };
    return (
        <div className="flex items-center gap-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${t.bg}`}>
                {t.icon} {FN_LABEL[fn] || fn}
            </span>
            {fn === "mechaniker" && specialty && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-orange-50 text-orange-800 border border-orange-200">
                    {SPEC_LABEL[specialty] || specialty}
                </span>
            )}
        </div>
    );
}

// Jun-2026 (User-Wunsch) — Profilfoto-Upload für Mitarbeiter (Avatar im Header).
function EmployeePhotoField({ value, onChange, firstName, lastName, testIdPrefix = "emp-photo" }) {
    const [busy, setBusy] = useState(false);
    const initials = `${(firstName || "").trim()} ${(lastName || "").trim()}`
        .split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Bitte JPG, PNG oder WebP wählen."); return;
        }
        if (file.size > 5 * 1024 * 1024) { toast.error("Maximal 5 MB."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(r.data?.url || r.data?.path || "");
            toast.success("Foto hochgeladen");
        } catch (_e) {
            toast.error("Foto-Upload fehlgeschlagen");
        } finally { setBusy(false); }
    };
    return (
        <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-row`}>
            <div className="w-16 h-16 rounded-full bg-[#111827] text-[#FFCC00] flex items-center justify-center font-display font-black text-lg shrink-0 overflow-hidden border border-gray-200">
                {value ? (
                    <img src={value} alt="Profilfoto" className="w-full h-full object-cover" data-testid={`${testIdPrefix}-img`} />
                ) : initials}
            </div>
            <div className="flex-1 min-w-0">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Profilfoto</label>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-gray-300 text-xs font-bold uppercase tracking-wider cursor-pointer hover:bg-gray-50 ${busy ? "opacity-50 cursor-wait" : ""}`} data-testid={`${testIdPrefix}-upload-label`}>
                        <Upload className="w-3 h-3" /> {busy ? "Lädt…" : (value ? "Ändern" : "Hochladen")}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={onFile} data-testid={`${testIdPrefix}-upload-input`} />
                    </label>
                    {value && (
                        <button type="button" onClick={() => onChange("")} className="text-xs font-bold uppercase tracking-wider text-red-700 hover:text-red-900 px-2 py-1" data-testid={`${testIdPrefix}-remove`}>
                            Entfernen
                        </button>
                    )}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Erscheint als Avatar im Header, wenn dieser Mitarbeiter als Ansprechpartner hinterlegt ist. Max. 5 MB.</p>
            </div>
        </div>
    );
}


export default function EmployeesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [isHauptsitz, setIsHauptsitz] = useState(false);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(null); // null | "new"
    const [editModal, setEditModal] = useState(null); // employee | null — vollständiges Bearbeiten
    const [messageModal, setMessageModal] = useState(null); // employee | null
    const [linkFilter, setLinkFilter] = useState("all"); // all | linked | unlinked
    const [departments, setDepartments] = useState([]);

    const loadDepartments = useCallback(async () => {
        try {
            const r = await api.get("/dealer/departments");
            setDepartments(r.data?.items || []);
        } catch { setDepartments([]); }
    }, []);

    // Auto-Öffnen des "Neuer Mitarbeiter"-Modals via ?new=1 (Einstellungs-Section-Shortcut)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("new") === "1") setModalOpen("new");
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/employees");
            setItems(r.data.items || []);
            setIsHauptsitz(!!r.data.is_hauptsitz);
        } catch (_e) {
            setItems([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "b2b") navigate("/");
        if (user) { load(); loadDepartments(); }
    }, [user, navigate, load, loadDepartments]);

    const remove = async (id) => {
        if (!window.confirm("Mitarbeiter wirklich löschen?")) return;
        try {
            await api.delete(`/dealer/employees/${id}`);
            toast.success("Mitarbeiter gelöscht");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const toggleActive = async (emp) => {
        try {
            await api.patch(`/dealer/employees/${emp.id}`, { is_active: !emp.is_active });
            toast.success(emp.is_active ? "Deaktiviert" : "Aktiviert");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    // Jul-2026 — Einladung (persönlicher Zugang) an Mitarbeiter ohne Login senden.
    const inviteEmployee = async (emp) => {
        try {
            const { data } = await api.post(`/dealer/employees/${emp.id}/invite`);
            const url = data?.invitation?.invite_url;
            if (url) {
                try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
                toast.success("Einladung erstellt — Link in Zwischenablage kopiert (Dev-Modus).", { duration: 6000 });
            } else {
                toast.success("Einladung per E-Mail versendet.");
            }
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Einladung fehlgeschlagen"); }
    };

    if (!user) return <div className="p-8">Lade…</div>;

    const linkedCount = items.filter((e) => e.linked_user_id).length;
    const unlinkedCount = items.length - linkedCount;
    const filtered = linkFilter === "linked" ? items.filter((e) => e.linked_user_id)
        : linkFilter === "unlinked" ? items.filter((e) => !e.linked_user_id)
        : items;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="employees-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/profile" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-profile">
                        <ArrowLeft className="w-3 h-3" /> Zurück zum Profil
                    </Link>
                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <Users className="w-7 h-7 text-[#FFCC00]" />
                            <div>
                                <h1 className="font-display font-black text-4xl md:text-5xl">Mitarbeiter</h1>
                                <p className="text-sm text-gray-300 mt-1">Verkauf · Kundendienst · Mechaniker — mit oder ohne Login</p>
                            </div>
                        </div>
                        <Button onClick={() => setModalOpen("new")} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="new-employee-btn">
                            <Plus className="w-4 h-4 mr-1" /> Neuer Mitarbeiter
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
                <DepartmentsManager onChanged={() => { loadDepartments(); load(); }} />
                {loading ? (
                    <div className="text-sm text-gray-500">Lade…</div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="employees-empty">
                        <Users className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                        <p className="font-bold text-gray-700">Noch keine Mitarbeiter</p>
                        <p className="text-sm text-gray-500 mt-1">Lege deinen ersten Mitarbeiter an — wahlweise mit Login (Zweiradverkäufer mit Zugang zur Werkstatt-App) oder ohne Login (rein für Auftrags-Zuweisung & Stempeluhr).</p>
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="employees-list">
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 flex-wrap" data-testid="employees-link-filter">
                            {[
                                { key: "all", label: "Alle", n: items.length },
                                { key: "linked", label: "Login verknüpft", n: linkedCount },
                                { key: "unlinked", label: "Ohne Login-Bezug", n: unlinkedCount },
                            ].map((f) => (
                                <button key={f.key} onClick={() => setLinkFilter(f.key)}
                                    className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-bold border transition-colors ${linkFilter === f.key ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
                                    data-testid={`employees-filter-${f.key}`}>
                                    {f.key === "linked" && <Link2 className="w-3 h-3" />}
                                    {f.label} <span className={linkFilter === f.key ? "text-[#FFCC00]" : "text-gray-400"}>{f.n}</span>
                                </button>
                            ))}
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="text-left px-3 py-2">Name</th>
                                    <th className="text-left px-3 py-2">Abteilung</th>
                                    <th className="text-left px-3 py-2">Funktion</th>
                                    {isHauptsitz && <th className="text-left px-3 py-2">Standort</th>}
                                    <th className="text-left px-3 py-2">Login / E-Mail</th>
                                    <th className="text-center px-3 py-2">Status</th>
                                    <th className="text-left px-3 py-2">Angelegt</th>
                                    <th className="text-right px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((e) => {
                                    const hasLogin = e.has_login !== false;
                                    return (
                                        <tr key={e.id} onClick={() => setEditModal(e)} className="border-t border-gray-100 cursor-pointer hover:bg-amber-50/50 transition-colors" title="Zum Bearbeiten klicken" data-testid={`employee-row-${e.id}`}>
                                            <td className="px-3 py-2 font-bold">
                                                <div className="flex items-center gap-2">
                                                    {e.profile_photo_url ? (
                                                        <img src={e.profile_photo_url} alt={e.name} className="w-8 h-8 rounded-full object-cover border border-gray-200 shrink-0" data-testid={`employee-avatar-${e.id}`} />
                                                    ) : (
                                                        <span className="w-8 h-8 rounded-full bg-[#111827] text-[#FFCC00] inline-flex items-center justify-center text-[10px] font-black shrink-0" data-testid={`employee-avatar-${e.id}`}>
                                                            {(e.name || "?").split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                                        </span>
                                                    )}
                                                    <span>{e.name}</span>
                                                    {e.linked_user_id && (
                                                        <span
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5 shrink-0"
                                                            title={e.linked_user_name ? `Verknüpft mit Login: ${e.linked_user_name}${e.linked_user_email ? " (" + e.linked_user_email + ")" : ""}` : "Mit Login-Konto verknüpft"}
                                                            data-testid={`employee-linked-${e.id}`}
                                                        >
                                                            <Link2 className="w-3 h-3" /> Login
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2" data-testid={`employee-department-${e.id}`}>
                                                {e.department_name ? (
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-sm px-1.5 py-0.5">{e.department_name}</span>
                                                ) : <span className="text-xs text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-2" data-testid={`employee-function-${e.id}`}>
                                                <FunctionBadge fn={e.employee_function} specialty={e.mechanic_specialty} />
                                            </td>
                                            {isHauptsitz && (
                                                <td className="px-3 py-2" data-testid={`employee-site-${e.id}`}>
                                                    {e.site_code ? (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600 bg-gray-100 border border-gray-200 rounded-sm px-1.5 py-0.5" title={e.site_name || e.site_code}>{e.site_code}</span>
                                                    ) : <span className="text-xs text-gray-300">—</span>}
                                                </td>
                                            )}
                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                {hasLogin ? (
                                                    e.email
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-gray-500">
                                                        <ShieldOff className="w-3 h-3" /> Ohne Login
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <button onClick={(ev) => { ev.stopPropagation(); toggleActive(e); }} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${e.is_active === false ? "bg-gray-200 text-gray-600" : "bg-green-100 text-green-900"}`} data-testid={`employee-toggle-${e.id}`}>
                                                    {e.is_active === false ? <><XIcon className="w-3 h-3" /> Inaktiv</> : <><Check className="w-3 h-3" /> Aktiv</>}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-500 font-mono-num">{new Date(e.created_at).toLocaleDateString("de-DE")}</td>
                                            <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                                                {e.mobile_phone && (
                                                    <button
                                                        onClick={(ev) => { ev.stopPropagation(); setMessageModal(e); }}
                                                        className="p-1.5 hover:bg-green-50 rounded-sm text-green-700 inline-flex"
                                                        title={`Nachricht an ${e.mobile_phone}`}
                                                        data-testid={`employee-message-${e.id}`}
                                                    >
                                                        <MessageCircle className="w-3 h-3" />
                                                    </button>
                                                )}
                                                {!hasLogin && e.private_email && !e.linked_user_id && (
                                                    <button onClick={(ev) => { ev.stopPropagation(); inviteEmployee(e); }} className="p-1.5 hover:bg-violet-50 rounded-sm text-violet-700 inline-flex" title={`Einladung an ${e.private_email} senden (persönlicher Zugang)`} data-testid={`employee-invite-${e.id}`}>
                                                        <MailPlus className="w-3 h-3" />
                                                    </button>
                                                )}
                                                <button onClick={(ev) => { ev.stopPropagation(); setEditModal(e); }} className="p-1.5 hover:bg-gray-100 rounded-sm text-[#111827]" title="Mitarbeiter bearbeiten" data-testid={`employee-edit-${e.id}`}>
                                                    <Pencil className="w-3 h-3" />
                                                </button>
                                                <button onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} className="p-1.5 hover:bg-red-50 rounded-sm text-red-700" title="Löschen" data-testid={`employee-del-${e.id}`}>
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="mt-6 grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-sm p-4 text-xs text-blue-900">
                        <strong>Mitarbeiter MIT Login (Zweiradverkäufer):</strong>
                        <ul className="list-disc list-inside mt-1 space-y-0.5">
                            <li>Werkstatt-Aufträge anlegen, bearbeiten, abschließen</li>
                            <li>Werkstatt-Kalender einsehen und Termine planen</li>
                            <li>Kunden- und Artikel-Stammdaten <em>einsehen</em></li>
                        </ul>
                        <strong className="block mt-2">Was sie NICHT dürfen:</strong>
                        <ul className="list-disc list-inside mt-1 space-y-0.5">
                            <li>Rechnungen anzeigen/erstellen</li>
                            <li>Statistiken und Finanzdaten einsehen</li>
                            <li>Stammdaten anlegen/ändern</li>
                            <li>Andere Mitarbeiter verwalten</li>
                        </ul>
                    </div>
                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-sm p-4 text-xs text-amber-900">
                        <strong>Mitarbeiter OHNE Login (z.B. Werkstatt-Personal):</strong>
                        <ul className="list-disc list-inside mt-1 space-y-0.5">
                            <li>Wird nur als <strong>Name</strong> in der Stammdatenbank geführt</li>
                            <li>Kann für Werkstatt-Aufträge zugewiesen werden</li>
                            <li>Kann auf der Stempeluhr (Zeiterfassung) erscheinen</li>
                            <li>Hat <strong>keinen</strong> Online-Zugang zur Plattform</li>
                        </ul>
                        <p className="mt-2 text-[11px] italic">Praktisch für Mechaniker, Aufbereitung und Lehrlinge, die nicht in der Web-App arbeiten.</p>
                    </div>
                </div>
            </div>

            {modalOpen === "new" && (
                <NewEmployeeModal isHauptsitz={isHauptsitz} departments={departments} onClose={() => setModalOpen(null)} onCreated={() => { setModalOpen(null); load(); }} />
            )}
            {editModal && (
                <EditEmployeeModal employee={editModal} departments={departments} onClose={() => setEditModal(null)} onSaved={() => { setEditModal(null); load(); }} />
            )}
            {messageModal && (
                <MessageEmployeeModal employee={messageModal} onClose={() => setMessageModal(null)} />
            )}
        </div>
    );
}

function NewEmployeeModal({ onClose, onCreated, isHauptsitz, departments = [] }) {
    const [hasLogin, setHasLogin] = useState(true);
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [mobilePhone, setMobilePhone] = useState("");
    const [salutation, setSalutation] = useState("");
    const [privatePhone, setPrivatePhone] = useState("");
    const [extension, setExtension] = useState("");
    const [privateEmail, setPrivateEmail] = useState("");
    const [linkedUserId, setLinkedUserId] = useState("");
    const [loginMatch, setLoginMatch] = useState(null);
    useEffect(() => {
        if (linkedUserId) { setLoginMatch(null); return; }
        const em = privateEmail.trim().toLowerCase();
        if (!em || !em.includes("@")) { setLoginMatch(null); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/dealer/employees/login-match", { params: { email: em } });
                setLoginMatch(r.data?.match || null);
            } catch { setLoginMatch(null); }
        }, 500);
        return () => clearTimeout(t);
    }, [privateEmail, linkedUserId]);
    const [password, setPassword] = useState("");
    const [fn, setFn] = useState("");
    const [departmentId, setDepartmentId] = useState("");
    const [specialty, setSpecialty] = useState("");
    const [busy, setBusy] = useState(false);
    const [branchId, setBranchId] = useState("");
    const [branches, setBranches] = useState([]);
    const [photoUrl, setPhotoUrl] = useState("");
    // Menü-Preset passend zur Funktion (User kann per Checkbox anpassen)
    const [hiddenMenus, setHiddenMenus] = useState([]);
    const toggleMenu = (key) => setHiddenMenus((h) =>
        h.includes(key) ? h.filter((k) => k !== key) : [...h, key]);
    useEffect(() => { setHiddenMenus(MENU_PRESETS_BY_FUNCTION[fn] || []); }, [fn]);

    useEffect(() => {
        if (!isHauptsitz) return;
        api.get("/org/locations").then((r) => setBranches(r.data?.locations || [])).catch(() => setBranches([]));
    }, [isHauptsitz]);

    const doCreate = async (force) => {
        const targetBranch = branchId && branchId !== "self" ? branchId : null;
        await api.post("/dealer/employees", {
            salutation: salutation || null,
            first_name: firstName.trim() || null,
            last_name: lastName.trim(),
            mobile_phone: mobilePhone.trim() || null,
            private_phone: privatePhone.trim() || null,
            extension: extension.trim() || null,
            private_email: privateEmail.trim().toLowerCase() || null,
            linked_user_id: linkedUserId || null,
            email: hasLogin || email.trim() ? email.trim().toLowerCase() : null,
            password: hasLogin ? password : null,
            has_login: hasLogin,
            employee_function: fn || null,
            mechanic_specialty: fn === "mechaniker" ? (specialty || null) : null,
            department_id: departmentId || null,
            employer_dealer_id: targetBranch,
            profile_photo_url: photoUrl || null,
            menu_sections_hidden: hiddenMenus,
            force: !!force,
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!lastName.trim()) { toast.error("Nachname ist Pflicht"); return; }
        if (hasLogin) {
            if (!email.trim() || !email.includes("@")) { toast.error("E-Mail ist Pflicht bei Login"); return; }
            if (password.length < 6) { toast.error("Passwort min 6 Zeichen"); return; }
        }
        if (specialty && fn !== "mechaniker") {
            toast.error("Spezialität nur bei Funktion „Mechaniker"); return;
        }
        setBusy(true);
        try {
            await doCreate(false);
            toast.success(`Mitarbeiter ${firstName.trim()} ${lastName.trim()} angelegt`);
            onCreated();
        } catch (err) {
            const detail = err.response?.data?.detail;
            // Duplikat-Erkennung: Backend liefert 409 mit code 'duplicate_employee'.
            if (err.response?.status === 409 && detail?.code === "duplicate_employee") {
                if (window.confirm(detail.message || "Eintrag existiert evtl. schon. Trotzdem anlegen?")) {
                    try {
                        await doCreate(true);
                        toast.success(`Mitarbeiter ${firstName.trim()} ${lastName.trim()} angelegt`);
                        onCreated();
                    } catch (e2) {
                        toast.error(e2.response?.data?.detail?.message || e2.response?.data?.detail || "Anlegen fehlgeschlagen");
                    }
                }
            } else {
                toast.error(typeof detail === "string" ? detail : detail?.message || "Anlegen fehlgeschlagen");
            }
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="new-employee-modal">
            <div className="bg-white max-w-md w-full rounded-sm shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="font-display font-black text-lg">Neuer Mitarbeiter</div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="new-emp-close"><XIcon className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <EmployeePhotoField value={photoUrl} onChange={setPhotoUrl} firstName={firstName} lastName={lastName} testIdPrefix="new-emp-photo" />
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Anrede</label>
                        <select value={salutation} onChange={(e) => setSalutation(e.target.value)}
                            className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm mt-1"
                            data-testid="new-emp-salutation">
                            <option value="">— keine —</option>
                            <option value="Herr">Herr</option>
                            <option value="Frau">Frau</option>
                            <option value="Divers">Divers</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Vorname</label>
                            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={60} className="rounded-sm mt-1" data-testid="new-emp-first-name" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachname *</label>
                            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required maxLength={80} className="rounded-sm mt-1" data-testid="new-emp-last-name" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Handynummer</label>
                        <Input
                            type="tel"
                            value={mobilePhone}
                            onChange={(e) => setMobilePhone(e.target.value)}
                            placeholder="z.B. +49 170 1234567"
                            maxLength={32}
                            className="rounded-sm mt-1"
                            data-testid="new-emp-mobile-phone"
                        />
                        <p className="text-[10px] text-gray-500 mt-1">Für SMS/WhatsApp-Quick-Versand an den Monteur — bitte mit Ländervorwahl (+49…) eintragen.</p>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Private Rufnummer</label>
                        <Input type="tel" value={privatePhone} onChange={(e) => setPrivatePhone(e.target.value)} placeholder="z.B. +49 30 1234567" maxLength={32} className="rounded-sm mt-1" data-testid="new-emp-private-phone" />
                        <p className="text-[10px] text-gray-500 mt-1">Private Erreichbarkeit (nur intern sichtbar, kein Kundenkontakt).</p>
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Durchwahl</label>
                        <Input
                            value={extension}
                            onChange={(e) => setExtension(e.target.value)}
                            placeholder="z.B. -12"
                            maxLength={20}
                            className="rounded-sm mt-1 font-mono-num"
                            data-testid="new-emp-extension"
                        />
                        <p className="text-[10px] text-gray-500 mt-1">Erscheint im Bearbeiter-Block der E-Mail-Signatur bei manuell versendeten Mails.</p>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Private E-Mail</label>
                        <Input type="email" value={privateEmail} onChange={(ev) => setPrivateEmail(ev.target.value)} placeholder="z.B. name@gmail.com" maxLength={200} className="rounded-sm mt-1" data-testid="new-emp-private-email" />
                        <p className="text-[10px] text-gray-500 mt-1">Verknüpfung mit dem privaten Login (Vermittler/Verkäufer). Wichtig für Monteure mit nur privater Adresse.</p>
                        {linkedUserId ? (
                            <div className="mt-2 flex items-center justify-between gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-2.5 py-1.5" data-testid="new-emp-linked">
                                <span className="text-[11px] text-emerald-800 font-semibold">✓ Mit Login-Konto verknüpft</span>
                                <button type="button" onClick={() => setLinkedUserId("")} className="text-[11px] font-bold text-emerald-700 underline" data-testid="new-emp-unlink">Verknüpfung lösen</button>
                            </div>
                        ) : loginMatch ? (
                            <div className="mt-2 flex items-center justify-between gap-2 rounded-sm border border-blue-200 bg-blue-50 px-2.5 py-1.5" data-testid="new-emp-login-match">
                                <span className="text-[11px] text-blue-800">Login-Konto gefunden: <b>{loginMatch.name}</b> ({loginMatch.role_label})</span>
                                <button type="button" onClick={() => setLinkedUserId(loginMatch.id)} className="text-[11px] font-bold text-white bg-blue-600 rounded-sm px-2 py-0.5" data-testid="new-emp-link-btn">Verknüpfen</button>
                            </div>
                        ) : null}
                    </div>

                    {isHauptsitz && branches.length > 0 && (
                        <div data-testid="new-emp-branch-wrap">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Standort / Filiale</label>
                            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
                                className="w-full mt-1 h-10 border border-gray-300 rounded-sm px-2 text-sm" data-testid="new-emp-branch">
                                <option value="">Hauptsitz (Standard)</option>
                                {branches.filter((b) => !b.is_self).map((b) => (
                                    <option key={b.dealer_id} value={b.dealer_id}>{b.site_code} · {b.street || b.company_name}</option>
                                ))}
                            </select>
                            <p className="text-[10px] text-gray-500 mt-1">Der Mitarbeiter wird diesem Standort zugeordnet.</p>
                        </div>
                    )}

                    {/* Login-Toggle */}
                    <div className="rounded-sm border border-gray-200 p-3 bg-gray-50">
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="new-emp-has-login-label">
                            <input
                                type="checkbox"
                                checked={hasLogin}
                                onChange={(e) => setHasLogin(e.target.checked)}
                                className="w-4 h-4"
                                data-testid="new-emp-has-login"
                            />
                            <span className="text-sm font-bold">Mitarbeiter erhält Login</span>
                        </label>
                        <p className="text-[10px] text-gray-500 mt-1 ml-6">
                            {hasLogin
                                ? "Loggt sich mit eigenen Credentials ein und sieht die für seine Funktion relevanten Bereiche — z. B. Werkstatt-Aufträge & Kalender, Verkauf oder Vermittlung (gilt auch für Verkäufer & Vermittler)."
                                : "Wird nur als Name geführt — keine Online-Anmeldung. Für interne Werkstatt-Mitarbeiter (Mechaniker, Aufbereitung, Lehrlinge)."}
                        </p>
                    </div>

                    {hasLogin && (
                        <>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">E-Mail *</label>
                                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required={hasLogin} className="rounded-sm mt-1" data-testid="new-emp-email" />
                                <p className="text-[10px] text-gray-500 mt-1">Mitarbeiter loggt sich mit dieser E-Mail ein</p>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Initial-Passwort *</label>
                                <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required={hasLogin} minLength={6} placeholder="mind. 6 Zeichen" className="rounded-sm mt-1" data-testid="new-emp-password" />
                                <p className="text-[10px] text-gray-500 mt-1">Bitte sicher an den Mitarbeiter weitergeben — er kann es später selbst ändern.</p>
                            </div>
                        </>
                    )}

                    {!hasLogin && (
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">E-Mail (optional)</label>
                            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1" data-testid="new-emp-email" />
                            <p className="text-[10px] text-gray-500 mt-1">Nicht erforderlich. Wenn gesetzt, wird sie für interne Notizen geführt — kein Login möglich.</p>
                        </div>
                    )}

                    {/* Abteilung (Phase 3) — bestimmt die freigeschalteten Bereiche */}
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Abteilung</label>
                        <select
                            value={departmentId}
                            onChange={(e) => setDepartmentId(e.target.value)}
                            className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm"
                            data-testid="new-emp-department"
                        >
                            <option value="">— keine Abteilung —</option>
                            {departments.map((d) => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-gray-500 mt-1">Der Mitarbeiter erbt die freigeschalteten Bereiche seiner Abteilung. Abteilungen werden oben verwaltet.</p>
                    </div>

                    {/* Funktion */}
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Funktion</label>
                        <select
                            value={fn}
                            onChange={(e) => {
                                setFn(e.target.value);
                                if (e.target.value !== "mechaniker") setSpecialty("");
                            }}
                            className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm"
                            data-testid="new-emp-function"
                        >
                            <option value="">— keine Angabe —</option>
                            {FUNCTIONS.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Mechaniker-Spezialität (conditional) */}
                    {fn === "mechaniker" && (
                        <div className="rounded-sm border-l-4 border-amber-500 bg-amber-50 p-3" data-testid="new-emp-specialty-wrap">
                            <label className="text-xs font-bold uppercase tracking-wider text-amber-900">Spezialität · Priorisierung</label>
                            <select
                                value={specialty}
                                onChange={(e) => setSpecialty(e.target.value)}
                                className="w-full mt-1 rounded-sm border border-amber-300 px-3 py-2 text-sm bg-white"
                                data-testid="new-emp-specialty"
                            >
                                <option value="">— keine Spezialität —</option>
                                {SPECIALTIES.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                            <p className="text-[10px] text-amber-800 mt-1">Hilft bei der automatischen Auftragsverteilung im Werkstatt-Kalender.</p>
                        </div>
                    )}

                    <MenuVisibilityConfig hiddenMenus={hiddenMenus} onToggle={toggleMenu} testIdPrefix="new-emp" />

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" onClick={onClose} variant="outline" className="rounded-sm">Abbrechen</Button>
                        <Button type="submit" disabled={busy} className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider" data-testid="new-emp-submit">
                            {busy ? "Sende…" : "Mitarbeiter anlegen"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function EditEmployeeModal({ employee, onClose, onSaved, departments = [] }) {
    const synthetic = (employee.email || "").includes("@local.twowheels.info");
    const [firstName, setFirstName] = useState(employee.first_name || "");
    const [lastName, setLastName] = useState(employee.last_name || (employee.name || "").split(" ").slice(-1)[0] || "");
    const [mobilePhone, setMobilePhone] = useState(employee.mobile_phone || "");
    const [salutation, setSalutation] = useState(employee.salutation || "");
    const [privatePhone, setPrivatePhone] = useState(employee.private_phone || "");
    const [extension, setExtension] = useState(employee.extension || "");
    const [privateEmail, setPrivateEmail] = useState(employee.private_email || "");
    const [linkedUserId, setLinkedUserId] = useState(employee.linked_user_id || "");
    const [loginMatch, setLoginMatch] = useState(null);
    useEffect(() => {
        if (linkedUserId) { setLoginMatch(null); return; }
        const em = privateEmail.trim().toLowerCase();
        if (!em || !em.includes("@")) { setLoginMatch(null); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/dealer/employees/login-match", { params: { email: em } });
                setLoginMatch(r.data?.match || null);
            } catch { setLoginMatch(null); }
        }, 500);
        return () => clearTimeout(t);
    }, [privateEmail, linkedUserId]);
    const [hasLogin, setHasLogin] = useState(employee.has_login !== false);
    const [email, setEmail] = useState(synthetic ? "" : (employee.email || ""));
    const [password, setPassword] = useState("");
    const [fn, setFn] = useState(employee.employee_function || "");
    const [departmentId, setDepartmentId] = useState(employee.department_id || "");
    const [specialty, setSpecialty] = useState(employee.mechanic_specialty || "");
    const [isActive, setIsActive] = useState(employee.is_active !== false);
    const [photoUrl, setPhotoUrl] = useState(employee.profile_photo_url || "");
    const [hiddenMenus, setHiddenMenus] = useState(employee.menu_sections_hidden || []);
    const toggleMenu = (key) => setHiddenMenus((h) =>
        h.includes(key) ? h.filter((k) => k !== key) : [...h, key]);
    const [busy, setBusy] = useState(false);
    const [autoSavedAt, setAutoSavedAt] = useState(null);
    const dirtyRef = useRef(false);

    const buildPayload = () => ({
        salutation: salutation || null,
        first_name: firstName.trim() || null,
        last_name: lastName.trim(),
        mobile_phone: mobilePhone.trim() || null,
        private_phone: privatePhone.trim() || null,
        extension: extension.trim() || null,
        private_email: privateEmail.trim().toLowerCase() || null,
        linked_user_id: linkedUserId || null,
        has_login: hasLogin,
        email: hasLogin ? email.trim().toLowerCase() : (email.trim().toLowerCase() || null),
        employee_function: fn || null,
        mechanic_specialty: fn === "mechaniker" ? (specialty || null) : null,
        department_id: departmentId || null,
        is_active: isActive,
        profile_photo_url: photoUrl || null,
        menu_sections_hidden: hiddenMenus,
    });

    // Jun-2026 (User-Wunsch) — Automatisches Sichern (1,2 s nach der letzten Änderung)
    useEffect(() => {
        if (!dirtyRef.current) { dirtyRef.current = true; return undefined; }
        if (!lastName.trim()) return undefined;
        if (hasLogin && (!email.trim() || !email.includes("@"))) return undefined;
        if (privateEmail.trim() && !privateEmail.includes("@")) return undefined;
        const t = setTimeout(async () => {
            try {
                await api.patch(`/dealer/employees/${employee.id}`, buildPayload());
                setAutoSavedAt(new Date());
            } catch { /* stiller Fehler — manuelles Speichern bleibt möglich */ }
        }, 1200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salutation, firstName, lastName, mobilePhone, privatePhone, extension, privateEmail, linkedUserId, hasLogin, email, fn, specialty, departmentId, isActive, photoUrl, hiddenMenus]);

    const submit = async (e) => {
        e.preventDefault();
        if (!lastName.trim()) { toast.error("Nachname ist Pflicht"); return; }
        if (hasLogin && (!email.trim() || !email.includes("@"))) { toast.error("E-Mail ist Pflicht bei Login"); return; }
        if (password && password.length < 6) { toast.error("Passwort min. 6 Zeichen"); return; }
        if (privateEmail.trim() && !privateEmail.includes("@")) { toast.error("Private E-Mail ist ungültig"); return; }
        if (specialty && fn !== "mechaniker") { toast.error("Spezialität nur bei „Mechaniker"); return; }
        setBusy(true);
        try {
            const payload = buildPayload();
            if (password) payload.password = password;
            await api.patch(`/dealer/employees/${employee.id}`, payload);
            toast.success("Mitarbeiter gespeichert");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="edit-employee-modal">
            <div className="bg-white max-w-md w-full rounded-sm shadow-xl max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="font-display font-black text-lg">Mitarbeiter bearbeiten</div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="edit-emp-close"><XIcon className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <EmployeePhotoField value={photoUrl} onChange={setPhotoUrl} firstName={firstName} lastName={lastName} testIdPrefix="edit-emp-photo" />
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Anrede</label>
                        <select value={salutation} onChange={(ev) => setSalutation(ev.target.value)}
                            className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm mt-1"
                            data-testid="edit-emp-salutation">
                            <option value="">— keine —</option>
                            <option value="Herr">Herr</option>
                            <option value="Frau">Frau</option>
                            <option value="Divers">Divers</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Vorname</label>
                            <Input value={firstName} onChange={(ev) => setFirstName(ev.target.value)} maxLength={60} className="rounded-sm mt-1" data-testid="edit-emp-first-name" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachname *</label>
                            <Input value={lastName} onChange={(ev) => setLastName(ev.target.value)} required maxLength={80} className="rounded-sm mt-1" data-testid="edit-emp-last-name" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Handynummer</label>
                        <Input type="tel" value={mobilePhone} onChange={(ev) => setMobilePhone(ev.target.value)} placeholder="z.B. +49 170 1234567" maxLength={32} className="rounded-sm mt-1" data-testid="edit-emp-mobile-phone" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Durchwahl</label>
                        <Input value={extension} onChange={(ev) => setExtension(ev.target.value)} placeholder="z.B. -12" maxLength={20} className="rounded-sm mt-1 font-mono-num" data-testid="edit-emp-extension" />
                        <p className="text-[10px] text-gray-500 mt-1">Erscheint im Bearbeiter-Block der E-Mail-Signatur.</p>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Private E-Mail</label>
                        <Input type="email" value={privateEmail} onChange={(ev) => setPrivateEmail(ev.target.value)} placeholder="z.B. john.ekeagu@gmail.com" maxLength={200} className="rounded-sm mt-1" data-testid="edit-emp-private-email" />
                        <p className="text-[10px] text-gray-500 mt-1">Verknüpft den Mitarbeiter mit seinem privaten Login (Vermittler/Verkäufer). Wichtig für Werkstatt-Monteure, die oft nur eine private Adresse haben.</p>
                        {linkedUserId ? (
                            <div className="mt-2 flex items-center justify-between gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-2.5 py-1.5" data-testid="edit-emp-linked">
                                <span className="text-[11px] text-emerald-800 font-semibold">✓ Mit Login-Konto verknüpft</span>
                                <button type="button" onClick={() => setLinkedUserId("")} className="text-[11px] font-bold text-emerald-700 underline" data-testid="edit-emp-unlink">Verknüpfung lösen</button>
                            </div>
                        ) : loginMatch ? (
                            <div className="mt-2 flex items-center justify-between gap-2 rounded-sm border border-blue-200 bg-blue-50 px-2.5 py-1.5" data-testid="edit-emp-login-match">
                                <span className="text-[11px] text-blue-800">Login-Konto gefunden: <b>{loginMatch.name}</b> ({loginMatch.role_label})</span>
                                <button type="button" onClick={() => setLinkedUserId(loginMatch.id)} className="text-[11px] font-bold text-white bg-blue-600 rounded-sm px-2 py-0.5" data-testid="edit-emp-link-btn">Verknüpfen</button>
                            </div>
                        ) : null}
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Private Rufnummer</label>
                        <Input type="tel" value={privatePhone} onChange={(ev) => setPrivatePhone(ev.target.value)} placeholder="z.B. +49 30 1234567" maxLength={32} className="rounded-sm mt-1" data-testid="edit-emp-private-phone" />
                        <p className="text-[10px] text-gray-500 mt-1">Private Erreichbarkeit (nur intern sichtbar, kein Kundenkontakt).</p>
                    </div>
                    <div className="rounded-sm border border-gray-200 p-3 bg-gray-50 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-emp-has-login-label">
                            <input type="checkbox" checked={hasLogin} onChange={(ev) => setHasLogin(ev.target.checked)} className="w-4 h-4" data-testid="edit-emp-has-login" />
                            <span className="text-sm font-bold">Mitarbeiter erhält Login</span>
                        </label>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">{hasLogin ? "E-Mail *" : "E-Mail (optional)"}</label>
                            <Input type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} required={hasLogin} className="rounded-sm mt-1" data-testid="edit-emp-email" />
                        </div>
                        {hasLogin && (
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Neues Passwort</label>
                                <Input type="text" value={password} onChange={(ev) => setPassword(ev.target.value)} minLength={6} placeholder="leer lassen = unverändert" className="rounded-sm mt-1" data-testid="edit-emp-password" />
                                <p className="text-[10px] text-gray-500 mt-1">Nur ausfüllen, um das Passwort zu ändern (min. 6 Zeichen).</p>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Abteilung</label>
                        <select value={departmentId} onChange={(ev) => setDepartmentId(ev.target.value)}
                            className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="edit-emp-department">
                            <option value="">— keine Abteilung —</option>
                            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                        <p className="text-[10px] text-gray-500 mt-1">Erbt die freigeschalteten Bereiche der Abteilung.</p>
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Funktion</label>
                        <select value={fn} onChange={(ev) => { setFn(ev.target.value); if (ev.target.value !== "mechaniker") setSpecialty(""); }}
                            className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="edit-emp-function">
                            <option value="">— keine Angabe —</option>
                            {FUNCTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                    </div>
                    {fn === "mechaniker" && (
                        <div className="rounded-sm border-l-4 border-amber-500 bg-amber-50 p-3" data-testid="edit-emp-specialty-wrap">
                            <label className="text-xs font-bold uppercase tracking-wider text-amber-900">Spezialität</label>
                            <select value={specialty} onChange={(ev) => setSpecialty(ev.target.value)}
                                className="w-full mt-1 rounded-sm border border-amber-300 px-3 py-2 text-sm bg-white" data-testid="edit-emp-specialty">
                                <option value="">— keine Spezialität —</option>
                                {SPECIALTIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="rounded-sm border border-gray-200 p-3 bg-gray-50" data-testid="edit-emp-menu-config">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Sichtbare Menüpunkte (Header-Dropdown)</label>
                        <p className="text-[10px] text-gray-500 mt-0.5 mb-2">Abgewählte Bereiche/Unterpunkte erscheinen für diesen Mitarbeiter nicht im Menü.</p>
                        <MenuSectionsChecklist hiddenMenus={hiddenMenus} onToggle={toggleMenu} testIdPrefix="edit-emp" />
                        <button type="button" onClick={() => setHiddenMenus(MENU_PRESETS_BY_FUNCTION[fn] || [])}
                            className="mt-2 text-[11px] font-bold text-blue-700 underline underline-offset-2"
                            data-testid="edit-emp-menu-preset-btn">
                            Preset für Funktion „{FN_LABEL[fn] || "—"}" anwenden
                        </button>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-emp-active-label">
                        <input type="checkbox" checked={isActive} onChange={(ev) => setIsActive(ev.target.checked)} className="w-4 h-4" data-testid="edit-emp-active" />
                        <span className="text-sm font-bold">Aktiv</span>
                    </label>

                    <div className="flex items-center justify-between gap-2 pt-2">
                        <span className="text-[10px] text-emerald-700 font-bold" data-testid="edit-emp-autosave-state">
                            {autoSavedAt ? `✓ Automatisch gespeichert ${autoSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : "Änderungen werden automatisch gespeichert"}
                        </span>
                        <div className="flex gap-2">
                            <Button type="button" onClick={onClose} variant="outline" className="rounded-sm">Schließen</Button>
                            <Button type="submit" disabled={busy} className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider" data-testid="edit-emp-submit">
                                {busy ? "Speichere…" : "Fertig"}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────────
//  MessageEmployeeModal — Phase 4c++++
//  Template-Auswahl + WhatsApp / SMS-Quick-Versand
// ──────────────────────────────────────────────────────────────────────────
const MSG_TEMPLATES = [
    {
        id: "reminder_today",
        title: "Termin-Reminder",
        text: "Hallo {name}, kurze Erinnerung: Heute steht dein Werkstatt-Termin an. Bitte melde dich, falls es Probleme gibt.",
    },
    {
        id: "order_ready",
        title: "Auftrag fertig",
        text: "Hallo {name}, der Auftrag ist fertig — bitte komm vorbei oder ruf zurück, wenn du Fragen hast.",
    },
    {
        id: "callback",
        title: "Bitte zurückrufen",
        text: "Hallo {name}, kannst du mich kurz zurückrufen? Wir brauchen deinen Input.",
    },
    {
        id: "shift_change",
        title: "Schicht-Änderung",
        text: "Hallo {name}, kurze Info: deine Schicht hat sich geändert. Bitte melde dich kurz, sobald du es gesehen hast.",
    },
    {
        id: "custom",
        title: "Eigene Nachricht",
        text: "",
    },
];

function MessageEmployeeModal({ employee, onClose }) {
    const [selected, setSelected] = useState(MSG_TEMPLATES[0].id);
    const tpl = MSG_TEMPLATES.find((t) => t.id === selected) || MSG_TEMPLATES[0];
    const firstName = (employee.first_name || (employee.name || "").split(" ")[0] || "").trim() || "Hallo";
    const [text, setText] = useState((tpl.text || "").replace("{name}", firstName));

    const onTemplateChange = (id) => {
        setSelected(id);
        const t = MSG_TEMPLATES.find((x) => x.id === id) || MSG_TEMPLATES[0];
        setText((t.text || "").replace("{name}", firstName));
    };

    const phone = (employee.mobile_phone || "").replace(/^\+/, "");
    const encodedText = encodeURIComponent(text);
    const waUrl = `https://wa.me/${phone}?text=${encodedText}`;
    const smsUrl = `sms:${employee.mobile_phone}?body=${encodedText}`;

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="message-modal">
            <div className="bg-white max-w-md w-full rounded-sm shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
                    <div>
                        <div className="font-display font-black text-lg">Nachricht senden</div>
                        <div className="text-xs text-gray-500 font-mono-num">an {employee.name} · {employee.mobile_phone}</div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="msg-close"><XIcon className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Template</label>
                        <select
                            value={selected}
                            onChange={(e) => onTemplateChange(e.target.value)}
                            className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm"
                            data-testid="msg-template"
                        >
                            {MSG_TEMPLATES.map((t) => (
                                <option key={t.id} value={t.id}>{t.title}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachricht</label>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={5}
                            maxLength={1500}
                            placeholder="Tippe deine Nachricht…"
                            className="w-full mt-1 rounded-sm border border-gray-300 p-2 text-sm font-mono-num"
                            data-testid="msg-text"
                        />
                        <p className="text-[10px] text-gray-500 mt-1">
                            {text.length}/1500 Zeichen · {`{name}`} wird mit Vornamen ersetzt
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2">
                        <a
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-green-600 hover:bg-green-700 text-white text-center font-black px-4 py-3 rounded-sm uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2"
                            data-testid="msg-send-whatsapp"
                            onClick={() => setTimeout(onClose, 200)}
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                        </a>
                        <a
                            href={smsUrl}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-center font-black px-4 py-3 rounded-sm uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2"
                            data-testid="msg-send-sms"
                            onClick={() => setTimeout(onClose, 200)}
                        >
                            <Phone className="w-4 h-4" /> SMS
                        </a>
                    </div>
                    <p className="text-[10px] text-gray-500 text-center pt-1">
                        Die Nachricht öffnet sich in WhatsApp Web/App bzw. der SMS-App deines Geräts — du bestätigst sie dort manuell.
                    </p>
                </div>
            </div>
        </div>
    );
}
